import { useQuery } from '@tanstack/react-query';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import TimelineIcon from '@mui/icons-material/Timeline';
import { useDataProvider, useTranslate } from 'react-admin';
import { Link } from 'react-router-dom';
import { format, subMonths } from 'date-fns';
import StatsWithIcon from './controls/StatsWithIcon';
import { numberFormatter } from './common';
import { ConfirmationRole, DataConfirmationStats } from './types';

export const confirmationRoles: ConfirmationRole[] = [
    'teacher',
    'student',
    'pupil',
];
export const confirmationDefaults = () => {
    const now = new Date();
    return {
        from: format(subMonths(now, 1), 'yyyy-MM-dd'),
        to: format(now, 'yyyy-MM-dd'),
        last_login_from: format(subMonths(now, 12), 'yyyy-MM-dd'),
        group_by: 'day' as const,
    };
};

export type ConfirmationFilter = {
    from: string;
    to: string;
    last_login_from: string | null;
    group_by: 'day' | 'month';
};

export const useDataConfirmations = (
    filter: ConfirmationFilter,
    enabled = true
) => {
    const dataProvider = useDataProvider();
    return useQuery<DataConfirmationStats>({
        queryKey: ['dataconfirmations', filter],
        queryFn: async () => {
            const response: { data: DataConfirmationStats } =
                await dataProvider.getAny(
                    '/statistics/dataconfirmations',
                    `filter=${encodeURIComponent(JSON.stringify(filter))}`
                );
            return response.data;
        },
        enabled,
        staleTime: 300_000,
    });
};

export const ConfirmationSummary = ({
    data,
    role = 'all',
    compact = false,
}: {
    data: DataConfirmationStats;
    role?: ConfirmationRole | 'all';
    compact?: boolean;
}) => {
    const translate = useTranslate();
    const teacher = data.summary.find(item => item.role === 'teacher');
    const ratio = (count: number, total: number) =>
        `${numberFormatter.format(count)} / ${numberFormatter.format(total)}${total ? ` (${new Intl.NumberFormat('fi-FI', { style: 'percent', maximumFractionDigits: 1 }).format(count / total)})` : ''}`;
    return (
        <Box sx={{ overflowX: 'auto' }}>
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell>
                            {translate('statistics.dataconfirmations.role')}
                        </TableCell>
                        <TableCell align="right">
                            {translate(
                                'statistics.dataconfirmations.own_current'
                            )}
                        </TableCell>
                        <TableCell align="right">
                            {translate(
                                'statistics.dataconfirmations.requires_check'
                            )}
                        </TableCell>
                        {!compact && (
                            <TableCell align="right">
                                {translate(
                                    'statistics.dataconfirmations.school_current'
                                )}
                            </TableCell>
                        )}
                        {!compact && (
                            <TableCell align="right">
                                {translate(
                                    'statistics.dataconfirmations.school_requires_check'
                                )}
                            </TableCell>
                        )}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {confirmationRoles
                        .filter(value => role === 'all' || role === value)
                        .map(value => {
                            const row = data.summary.find(
                                item => item.role === value
                            );
                            return (
                                <TableRow key={value}>
                                    <TableCell>
                                        {translate(
                                            `statistics.dataconfirmations.roles.${value}`
                                        )}
                                    </TableCell>
                                    <TableCell align="right">
                                        {ratio(
                                            row?.own_current || 0,
                                            row?.total || 0
                                        )}
                                    </TableCell>
                                    <TableCell align="right">
                                        {ratio(
                                            row?.own_requires_check || 0,
                                            row?.total || 0
                                        )}
                                    </TableCell>
                                    {!compact && (
                                        <TableCell align="right">
                                            {value === 'teacher'
                                                ? ratio(
                                                      row?.school_current || 0,
                                                      row?.school_total || 0
                                                  )
                                                : '—'}
                                        </TableCell>
                                    )}
                                    {!compact && (
                                        <TableCell align="right">
                                            {value === 'teacher'
                                                ? ratio(
                                                      row?.school_requires_check ||
                                                          0,
                                                      row?.school_total || 0
                                                  )
                                                : '—'}
                                        </TableCell>
                                    )}
                                </TableRow>
                            );
                        })}
                </TableBody>
            </Table>
            {compact && (
                <Typography variant="body2" sx={{ p: 1.5 }}>
                    {translate('statistics.dataconfirmations.school_current')}:{' '}
                    {ratio(
                        teacher?.school_current || 0,
                        teacher?.school_total || 0
                    )}
                </Typography>
            )}
            {compact && (
                <Typography variant="body2" sx={{ px: 1.5, pb: 1.5 }}>
                    {translate(
                        'statistics.dataconfirmations.school_requires_check'
                    )}
                    :{' '}
                    {ratio(
                        teacher?.school_requires_check || 0,
                        teacher?.school_total || 0
                    )}
                </Typography>
            )}
        </Box>
    );
};

const DataConfirmations = () => {
    const translate = useTranslate();
    const { data, isPending, error } = useDataConfirmations(
        confirmationDefaults()
    );
    return (
        <StatsWithIcon
            to="/dataconfirmations"
            icon={FactCheckIcon}
            title={translate('statistics.dataconfirmations.title')}
            subtitle={translate('statistics.dataconfirmations.current')}
        >
            {isPending ? (
                <Box sx={{ p: 2 }}>
                    <CircularProgress size={24} />
                </Box>
            ) : error ? (
                <Alert severity="error">{error.message}</Alert>
            ) : (
                data && (
                    <>
                        <ConfirmationSummary data={data} compact />
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            component="p"
                            sx={{ px: 1.5 }}
                        >
                            {translate(
                                'statistics.dataconfirmations.widget_scope',
                                { days: data.recheck_days }
                            )}
                        </Typography>
                    </>
                )
            )}
            <Box sx={{ p: 1, textAlign: 'right' }}>
                <Button
                    component={Link}
                    to="/dataconfirmations"
                    size="small"
                    startIcon={<TimelineIcon />}
                >
                    {translate('statistics.dataconfirmations.open')}
                </Button>
            </Box>
        </StatsWithIcon>
    );
};

export default DataConfirmations;
